/**
 * useFlowLimit — the plan's knod cap, for the dashboard counter.
 *
 * `GET /api/users/me/entitlements` is the only way the browser can learn this:
 * `subscription_plans` is not RLS-readable from the client, and the count the
 * migration-099 trigger enforces includes archived flows, so the dashboard's
 * own listing cannot stand in for it either.
 *
 * Failure is silent by design. The counter is ambient information; if it can't
 * be loaded the dashboard should look exactly as it did before this feature
 * existed, not sprout an error. The refusal path (src/utils/flowLimit.ts) still
 * explains the cap at the moment it actually bites, and the database trigger is
 * the real enforcement either way.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import { UNLIMITED_FLOWS } from '../utils/flowLimit';
import logger from '../utils/logger';

export interface FlowLimitState {
  /** The plan's max_flows; -1 = unlimited. null until known (or on failure). */
  limit: number | null;
  /** Server-side row count, matching what the trigger counts. null until known. */
  used: number | null;
  /** True when the plan (or an admin exemption) imposes no cap. */
  unlimited: boolean;
  loading: boolean;
  refresh: () => void;
}

interface EntitlementsResponse {
  flows?: { used?: number; limit?: number; unlimited?: boolean };
}

export function useFlowLimit(): FlowLimitState {
  const [limit, setLimit] = useState<number | null>(null);
  const [used, setUsed] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // A response that lands after the dashboard is gone must not set state.
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    let ignored = false;

    (async () => {
      try {
        const res = await authFetch('/api/users/me/entitlements');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const body = (await res.json()) as EntitlementsResponse;
        if (ignored || cancelledRef.current) return;

        const nextLimit = body?.flows?.limit;
        const nextUsed = body?.flows?.used;
        setLimit(typeof nextLimit === 'number' ? nextLimit : null);
        setUsed(typeof nextUsed === 'number' ? nextUsed : null);
      } catch (error) {
        if (ignored || cancelledRef.current) return;
        // Leave limit null → the counter renders its placeholder and then
        // nothing, which is the pre-feature appearance.
        logger.warn('[useFlowLimit] Could not load the flow limit:', error);
        setLimit(null);
        setUsed(null);
      } finally {
        if (!ignored && !cancelledRef.current) setLoading(false);
      }
    })();

    return () => {
      ignored = true;
      cancelledRef.current = true;
    };
  }, [nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  return {
    limit,
    used,
    unlimited: limit === UNLIMITED_FLOWS,
    loading,
    refresh,
  };
}

export default useFlowLimit;
